import React, { useEffect, useState } from "react";
import OnboardingScreen, { OnboardingErrorText } from "../OnboardingScreen";
import { OnboardingCircleNextButton } from "../OnboardingButtons";
import { OnboardingAnimatedCheck } from "../OnboardingRows";
import type { OnboardingWellnessDraft } from "../../../hooks/useOnboardingWizard";
import {
  CAPACITY_OPTIONS,
  DEMO_WELLNESS_SCORE,
  FIRST_WELLNESS_INTRO,
  SLEEP_TIME_RESPONSES,
  WELLNESS_CATEGORIES,
  WELLNESS_FALLBACK_QUESTIONS,
  sorenessLabel,
  wellnessAnswerLabel,
  wellnessCategoryLabel,
} from "../onboardingCopy";
import { wellnessScore } from "../onboardingValidation";

interface FirstWellnessStepProps {
  wellness: OnboardingWellnessDraft;
  submitting: boolean;
  submitError: string | null;
  /** Today's log already exists — the form just advances. */
  alreadyLogged: boolean;
  /** False when there is no earlier step to return to from the intro. */
  showBack?: boolean;
  onChange: (patch: Partial<OnboardingWellnessDraft>) => void;
  onBack: () => void;
  onSubmit: () => void;
}

/** iOS `SliderColors.wellness` (1–7) and `SliderColors.soreness` (0–10). */
const WELLNESS_COLORS = ["#DC2626", "#F87171", "#FCA5A5", "#FDE68A", "#86EFAC", "#4ADE80", "#22C55E"];
const SORENESS_COLORS = ["#22C55E", "#34D399", "#6EE7B7", "#A7F3D0", "#D9F99D", "#FDE68A", "#FCA5A5", "#FB7185", "#F87171", "#EF4444", "#DC2626"];
const wellnessColor = (value: number) => WELLNESS_COLORS[Math.min(Math.max(value, 1), 7) - 1];
const sorenessColor = (value: number) => SORENESS_COLORS[Math.min(Math.max(value, 0), 10)];

/**
 * iOS `PeakSlider`: 12px track, value-coloured fill and 24px thumb with a 2px
 * ring, mono value badge. A transparent range input on top keeps keyboard
 * and screen-reader support.
 */
const PeakSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  color: string;
  onChange: (value: number) => void;
}> = ({ label, value, min, max, color, onChange }) => {
  const fraction = max > min ? (value - min) / (max - min) : 0;
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative h-7 flex-1">
        <div className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 rounded-full bg-[rgba(120,120,120,0.22)]" />
        <div
          className="absolute left-0 top-1/2 h-3 -translate-y-1/2 rounded-full transition-[background-color] duration-150"
          style={{ width: `calc(12px + (100% - 24px) * ${fraction})`, backgroundColor: color }}
        />
        <div
          className="absolute top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border-2 border-peak-bg transition-[background-color] duration-150"
          style={{ left: `calc((100% - 24px) * ${fraction})`, backgroundColor: color }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </div>
      <span className="min-w-[24px] text-right font-mono text-[15px] font-semibold text-peak-text">{value}</span>
    </div>
  );
};

/** iOS `WellnessSlider` inside a Caveat-titled section: question, grey answer box, slider. */
const WellnessSection: React.FC<{
  title: string;
  question: string;
  answer: string;
  value: number;
  min: number;
  max: number;
  color: string;
  onChange: (value: number) => void;
}> = ({ title, question, answer, value, min, max, color, onChange }) => (
  <section className="flex flex-col gap-2">
    <h2 className="font-caveat text-[20px] leading-tight text-peak-text-secondary">{title}</h2>
    <p className="font-funnel text-[16px] text-peak-text">{question}</p>
    <p className="rounded-md bg-peak-bg-secondary p-3 font-funnel text-[16px] font-bold text-peak-text">{answer}</p>
    <PeakSlider label={question} value={value} min={min} max={max} color={color} onChange={onChange} />
  </section>
);

/** iOS `FormCheckRow`: Funnel Medium 15, 52px min height, animated check on the right. */
const FormCheckRow: React.FC<{ title: string; checked: boolean; onToggle: () => void }> = ({ title, checked, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
    className="flex min-h-[52px] w-full items-center justify-between gap-3 px-4 text-left"
  >
    <span className="font-funnel text-[15px] font-medium text-peak-text">{title}</span>
    <OnboardingAnimatedCheck checked={checked} />
  </button>
);

const IntroRing: React.FC<{ on: boolean }> = ({ on }) => {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="relative h-[60px] w-[60px] shrink-0">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#10B981" strokeOpacity={0.2} strokeWidth={10} />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#10B981"
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - (on ? DEMO_WELLNESS_SCORE / 100 : 0))}
          style={{ transition: "stroke-dashoffset 800ms ease-out" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-geist text-[18px] font-bold text-peak-text">
        {DEMO_WELLNESS_SCORE}
      </span>
    </div>
  );
};

/** Intro page then the first real wellness questionnaire (iOS `OnboardingFirstWellnessPage` + `WellnessForm`). */
const FirstWellnessStep: React.FC<FirstWellnessStepProps> = ({
  wellness,
  submitting,
  submitError,
  alreadyLogged,
  showBack = true,
  onChange,
  onBack,
  onSubmit,
}) => {
  const [page, setPage] = useState<0 | 1>(0);
  const [ringOn, setRingOn] = useState(false);
  const [unwellOpen, setUnwellOpen] = useState(wellness.sick || wellness.injured);
  const score = wellnessScore(wellness);

  useEffect(() => {
    if (page !== 0) return;
    setRingOn(false);
    const t = setTimeout(() => setRingOn(true), 350);
    return () => clearTimeout(t);
  }, [page]);

  if (page === 0) {
    return (
      <OnboardingScreen
        showBack={showBack}
        onBack={onBack}
        scroll
        footer={
          <div className="flex justify-end">
            <OnboardingCircleNextButton down onClick={() => setPage(1)} />
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <p className="font-caveat text-[30px] leading-none text-peak-text-secondary">Add your first</p>
              <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">Wellness log</h1>
            </div>
            <IntroRing on={ringOn} />
          </div>
          <p className="font-funnel text-[22px] font-semibold leading-snug text-peak-text">{FIRST_WELLNESS_INTRO.lead}</p>
          <div className="flex flex-col gap-3">
            {FIRST_WELLNESS_INTRO.paragraphs.map((paragraph) => (
              <p key={paragraph} className="font-funnel text-[16px] leading-relaxed text-peak-text-secondary">{paragraph}</p>
            ))}
          </div>
          <p className="font-geist text-[9px] text-peak-text-secondary">{FIRST_WELLNESS_INTRO.reference}</p>
        </div>
      </OnboardingScreen>
    );
  }

  return (
    <OnboardingScreen
      onBack={() => setPage(0)}
      scroll
      footer={
        <div className="flex flex-col gap-3">
          <OnboardingErrorText>{submitError}</OnboardingErrorText>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="flex min-h-[56px] w-full items-center justify-center rounded-lg bg-peak-success font-funnel text-[16px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
          >
            {alreadyLogged ? "Continue" : submitting ? "Saving…" : `Save wellness log · ${score}`}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">
            Wellness <span className="font-caveat text-[34px] font-normal tracking-normal text-peak-text-secondary">Log</span>
          </h1>
          <p className="font-funnel text-[14px] text-peak-text-secondary">Self-reported wellness questionnaire.</p>
          <p className="font-geist text-[12px] text-peak-text-tertiary">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>

        {alreadyLogged && (
          <p className="rounded-md bg-peak-bg-secondary px-4 py-3 font-funnel text-[14px] text-peak-text-secondary">
            Today's wellness log is already saved. Continue when you're ready.
          </p>
        )}

        <WellnessSection
          title="Sleep duration"
          question="How long did you sleep?"
          answer={wellnessAnswerLabel(SLEEP_TIME_RESPONSES, wellness.sleepTime)}
          value={wellness.sleepTime}
          min={1}
          max={7}
          color={wellnessColor(wellness.sleepTime)}
          onChange={(sleepTime) => onChange({ sleepTime })}
        />
        {WELLNESS_CATEGORIES.map((category) => (
          <WellnessSection
            key={category}
            title={wellnessCategoryLabel(category)}
            question={WELLNESS_FALLBACK_QUESTIONS[category].question}
            answer={wellnessAnswerLabel(WELLNESS_FALLBACK_QUESTIONS[category].answers, wellness[category])}
            value={wellness[category]}
            min={1}
            max={7}
            color={wellnessColor(wellness[category])}
            onChange={(value) => onChange({ [category]: value } as Partial<OnboardingWellnessDraft>)}
          />
        ))}
        <WellnessSection
          title="Soreness"
          question="How sore are you today?"
          answer={sorenessLabel(wellness.soreness)}
          value={wellness.soreness}
          min={0}
          max={10}
          color={sorenessColor(wellness.soreness)}
          onChange={(soreness) => onChange({ soreness })}
        />

        <div className="flex flex-col gap-4 pt-1">
          <button
            type="button"
            onClick={() => setUnwellOpen((open) => !open)}
            aria-expanded={unwellOpen}
            className="flex items-center gap-1 self-start pb-1 pl-2 pt-3 font-funnel text-[12px] text-peak-text-tertiary"
          >
            Unwell or injured?
            <svg viewBox="0 0 24 24" className={`h-2.5 w-2.5 transition-transform ${unwellOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {unwellOpen && (
            <div className="rounded-[10px] border border-peak-border bg-peak-bg">
              <FormCheckRow title="Feeling unwell" checked={wellness.sick} onToggle={() => onChange({ sick: !wellness.sick })} />
              <div className="mx-4 h-px bg-peak-border" />
              <FormCheckRow title="Carrying an injury" checked={wellness.injured} onToggle={() => onChange({ injured: !wellness.injured })} />
              {wellness.injured && (
                <>
                  <div className="mx-4 h-px bg-peak-border" />
                  <div className="flex flex-col gap-2 px-4 pb-4 pt-3">
                    <p className="font-funnel text-[13px] font-medium text-peak-text-secondary">What training can you do?</p>
                    <div className="flex gap-2" role="radiogroup" aria-label="Training capacity">
                      {CAPACITY_OPTIONS.map((option) => {
                        const selected = wellness.trainingCapacity === option.wire;
                        return (
                          <button
                            key={option.wire}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => onChange({ trainingCapacity: option.wire })}
                            className={`flex-1 rounded-md border py-2.5 font-funnel text-[14px] font-medium transition-colors ${
                              selected ? "border-peak-brand bg-peak-brand-subtle text-peak-brand" : "border-peak-border bg-peak-bg text-peak-text"
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </OnboardingScreen>
  );
};

export default FirstWellnessStep;
